import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { uploadFiles } from "~/web/src/api";
import type { Channel } from "~/web/src/channel";
import { reportError } from "~/web/src/errors";

// Drag & drop anywhere on the workspace = upload. Listens on the document;
// while a file drag is over the window a fixed overlay shows the cue AND is
// what receives the drop — otherwise the BROWSER iframe, <video> or the
// textarea would take it. A depth counter keeps enter/leave over children
// from flickering the overlay.

const hasFiles = (e: DragEvent): boolean =>
  Array.from(e.dataTransfer?.types ?? []).includes("Files");

export function DropZone({ channel }: { channel: Channel }) {
  const over = useSignal(false);
  useEffect(() => {
    let depth = 0;
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth++;
      over.value = true;
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) over.value = false;
    };
    const dragover = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); // required, or the browser refuses the drop
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      over.value = false;
      const files = e.dataTransfer?.files;
      if (files?.length) {
        uploadFiles(channel.id, files, channel.org.value?.subscribed ?? false).catch(reportError);
      }
    };
    document.addEventListener("dragenter", enter);
    document.addEventListener("dragleave", leave);
    document.addEventListener("dragover", dragover);
    document.addEventListener("drop", drop);
    return () => {
      document.removeEventListener("dragenter", enter);
      document.removeEventListener("dragleave", leave);
      document.removeEventListener("dragover", dragover);
      document.removeEventListener("drop", drop);
    };
  }, [channel]);
  if (!over.value) return null;
  return (
    <div class="drop-overlay">
      <div class="drop-cue">Drop to upload to {channel.project.value?.name ?? "the project"}/</div>
    </div>
  );
}
